import { getStroke } from "perfect-freehand";
const DEFAULT_OPTIONS = {
    size: 6,
    thinning: 0.4,
    streamline: 0.5,
    smoothing: 0.5,
    simulatePressure: true,
};
/**
 * Convert an array of `[x, y]` outline points (the polygon getStroke
 * produces) into an SVG path string. Uses the README-canonical
 * quadratic-Bezier-through-midpoints construction so the polygon
 * fills without a visible polyline boundary at vertex joins.
 *
 * Returns an empty string for empty input (caller should skip the
 * path entirely in that case).
 */
function getSvgPathFromStroke(points) {
    if (points.length === 0)
        return "";
    if (points.length < 4) {
        // Two- or three-point strokes (a tap, basically) become a tiny
        // closed dot. Falls back to a literal polygon path.
        const [first, ...rest] = points;
        if (!first)
            return "";
        const segs = [`M ${first[0]} ${first[1]}`];
        for (const p of rest)
            segs.push(`L ${p[0]} ${p[1]}`);
        segs.push("Z");
        return segs.join(" ");
    }
    const d = [];
    let p0 = points[0];
    let p1 = points[1];
    d.push("M", p0[0], p0[1], "Q");
    for (let i = 1; i < points.length; i++) {
        d.push(p0[0], p0[1], (p0[0] + p1[0]) / 2, (p0[1] + p1[1]) / 2);
        p0 = p1;
        p1 = points[i];
    }
    d.push("Z");
    return d.join(" ");
}
/**
 * Convert a single stroke (in normalized 0–1 host coords) into an
 * SVG `path d=` string sized to the host's pixel rect. Returns
 * `undefined` for strokes that have no points or whose getStroke
 * output is empty (defensive — getStroke can return `[]` for
 * pathological inputs).
 */
export function strokeToSvgPath(stroke, hostWidth, hostHeight, options = DEFAULT_OPTIONS) {
    if (stroke.points.length === 0)
        return undefined;
    if (hostWidth <= 0 || hostHeight <= 0)
        return undefined;
    const opts = { ...DEFAULT_OPTIONS, ...options };
    // Map normalized points → pixel coords. perfect-freehand expects
    // `[x, y, pressure?]`; pressure is optional and only used when
    // `simulatePressure: false` (devices that report it).
    const pixelPoints = stroke.points.map((p) => [p.x * hostWidth, p.y * hostHeight, p.pressure ?? 0.5]);
    const outline = getStroke(pixelPoints, {
        size: opts.size,
        thinning: opts.thinning,
        streamline: opts.streamline,
        smoothing: opts.smoothing,
        simulatePressure: opts.simulatePressure,
    });
    if (outline.length === 0)
        return undefined;
    return getSvgPathFromStroke(outline);
}
